import React, { useState, useEffect, useCallback } from "react";
import { Card, Badge, Button, Spinner, Alert } from "react-bootstrap";
import WhatsAppService from "services/WhatsAppService";
import SettingsService from "services/SettingsService";
import WhatsAppPolicy from "./WhatsAppPolicy";
import { handleErrors } from "jsx/components/CitoPlus/components/ErrorHandlingComponent";
import {
  notifySuccess,
  notifyInfo,
  notifyError,
} from "jsx/components/CitoPlus/components/CitoPlusToastr";
import { SpinnerCitoPlus } from "jsx/components/CitoPlus/components/loadingLogo";
import CountdownTimer from "jsx/components/CitoPlus/components/CountdownTimer";

// Constantes de WhatsAppStatus (deben coincidir con backend)
const WHATSAPP_STATUS = {
  NOT_EXISTS: "not_exists",
  CREATED: "created",
  WAITING_QR: "qr_needed",
  CONNECTING: "connecting",
  CONNECTED: "open",
  DISCONNECTED: "close",
  ERROR: "error",
};

const WhatsAppQRAdmin = () => {
  const [qrCode, setQrCode] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [qrLoading, setQrLoading] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [polling, setPolling] = useState(false);
  const [pollingController, setPollingController] = useState(null);
  const [autoCreationAttempted, setAutoCreationAttempted] = useState(false);
  const [countdown, setCountdown] = useState(null);
  const [userStoppedPolling, setUserStoppedPolling] = useState(false);

  // Policy state
  const [policyAccepted, setPolicyAccepted] = useState(false);
  const [policyLoading, setPolicyLoading] = useState(true);

  // localStorage key
  const STORAGE_KEY = "whatsapp_admin_stopped";

  // Check localStorage for stopped state on component mount
  useEffect(() => {
    const stoppedState = localStorage.getItem(STORAGE_KEY);
    if (stoppedState === "true") {
      setUserStoppedPolling(true);
    }
  }, []);

  // Check WhatsApp policy on component mount
  useEffect(() => {
    const checkPolicy = async () => {
      try {
        setPolicyLoading(true);
        const policyData = await SettingsService.getWhatsAppPolicy();
        setPolicyAccepted(policyData.accepted);
      } catch (error) {
        handleErrors(error);
        // If error, assume not accepted to be safe
        setPolicyAccepted(false);
      } finally {
        setPolicyLoading(false);
      }
    };

    checkPolicy();
  }, []);

  // Expose function globally for clearing localStorage on logout
  useEffect(() => {
    window.clearWhatsAppAdminState = () => {
      localStorage.removeItem(STORAGE_KEY);
      setUserStoppedPolling(false);
    };
    return () => {
      delete window.clearWhatsAppAdminState;
    };
  }, []);

  const fetchStatus = useCallback(async () => {
    try {
      setLoading(true);
      const statusData = await WhatsAppService.getStatus();
      setStatus(statusData);
      const connected = statusData.status === WHATSAPP_STATUS.CONNECTED;
      setIsConnected(connected);

      // If connected, clear any stopped state
      if (connected) {
        setUserStoppedPolling(false);
        localStorage.removeItem(STORAGE_KEY);
      }

      // Si está cerrado (close/DISCONNECTED), marcar como stopped para evitar auto-reconexión
      // Esto previene que intente reconectar cuando la instancia se cerró por inactividad
      if (statusData.status === WHATSAPP_STATUS.DISCONNECTED) {
        setUserStoppedPolling(true);
        localStorage.setItem(STORAGE_KEY, "true");
        // Detener cualquier polling activo
        if (pollingController) {
          pollingController.stop();
          setPollingController(null);
        }
        setPolling(false);
        setQrCode(null);
        setCountdown(null);
      }
    } catch (error) {
      // If no instance exists, that's okay - user needs to register first
      if (error.response?.status !== 404) {
        handleErrors(error);
      }
    } finally {
      setLoading(false);
    }
  }, [pollingController]);

  const stopStatusPolling = useCallback(() => {
    if (pollingController) {
      pollingController.stop();
      setPollingController(null);
    }
    setPolling(false);
    setCountdown(null);
    setQrCode(null);
    setShowInstructions(false);
    setUserStoppedPolling(true);
    // Save to localStorage
    localStorage.setItem(STORAGE_KEY, "true");
  }, [pollingController]);

  const handlePolicyAccepted = useCallback(() => {
    setPolicyAccepted(true);
  }, []);

  const startStatusPolling = useCallback(async () => {
    if (pollingController) {
      pollingController.stop();
    }

    setPolling(true);

    const controller = await WhatsAppService.startStatusPolling({
      onStatusUpdate: (statusData) => {
        setStatus(statusData);
        setIsConnected(statusData.status === WHATSAPP_STATUS.CONNECTED);
      },
      onConnected: (statusData) => {
        setStatus(statusData);
        setIsConnected(true);
        setQrCode(null);
        setShowInstructions(false);
        setPolling(false);
        setPollingController(null);
        setCountdown(null);
        setUserStoppedPolling(false);
        localStorage.removeItem(STORAGE_KEY);
        if (statusData.data?.whatsapp_name) {
          notifySuccess(
            `¡WhatsApp conectado exitosamente! Conectado como: ${statusData.data.whatsapp_name}`,
          );
        } else {
          notifySuccess("¡WhatsApp conectado exitosamente!");
        }
      },
      onTimeout: () => {
        setQrCode(null);
        setShowInstructions(false);
        setPolling(false);
        setPollingController(null);
        setCountdown(null);
        setUserStoppedPolling(true);
        localStorage.setItem(STORAGE_KEY, "true");
        notifyInfo(
          "Se detuvo la verificación automática después de 5 minutos. Puedes generar un nuevo QR.",
        );
      },
      onCountdown: (countdownData) => {
        setCountdown(countdownData);
      },
      timeoutMinutes: 5,
      intervalSeconds: 3,
    });

    setPollingController(controller);
  }, [pollingController]);

  const fetchQRCode = useCallback(async () => {
    try {
      setQrLoading(true);
      const qrData = await WhatsAppService.getQRCode();
      if (qrData.qr_code) {
        setQrCode(qrData.qr_code);
        setShowInstructions(true);
        setUserStoppedPolling(false);
        localStorage.removeItem(STORAGE_KEY);
        startStatusPolling();
        notifyInfo(
          "Nuevo código QR generado con token actualizado. Escanéalo con tu teléfono para conectar WhatsApp.",
        );
      } else {
        notifyError("No se pudo obtener el código QR. Intenta nuevamente.");
      }
    } catch (error) {
      handleErrors(error);
    } finally {
      setQrLoading(false);
    }
  }, [startStatusPolling]);

  const handleCreateInstance = useCallback(async () => {
    try {
      setLoading(true);
      const response = await WhatsAppService.createInstance();

      if (response.qr_code) {
        setQrCode(response.qr_code);
        setShowInstructions(true);
        setUserStoppedPolling(false);
        localStorage.removeItem(STORAGE_KEY);
        startStatusPolling();
        notifySuccess(
          "Instancia creada exitosamente. Escanea el código QR para conectar.",
        );
      } else if (
        response.status === "connected" ||
        response.status === "open"
      ) {
        notifyInfo("La instancia ya está conectada a WhatsApp.");
        setIsConnected(true);
        setUserStoppedPolling(false);
        localStorage.removeItem(STORAGE_KEY);
        stopStatusPolling();
      } else {
        notifySuccess("Instancia creada. Obteniendo código QR...");
        await fetchQRCode();
      }

      await fetchStatus();
    } catch (error) {
      handleErrors(error);
    } finally {
      setLoading(false);
    }
  }, [startStatusPolling, stopStatusPolling, fetchQRCode, fetchStatus]);

  const handleDisconnect = useCallback(async () => {
    try {
      setLoading(true);
      stopStatusPolling();
      const response = await WhatsAppService.disconnect();

      // Show specific message based on disconnect result
      if (response.data?.evolution_disconnect) {
        notifySuccess(
          "WhatsApp desconectado exitosamente desde Evolution API y base de datos.",
        );
      } else if (response.data?.evolution_error) {
        notifyInfo(
          `WhatsApp desconectado en base de datos. Evolution API: ${response.data.evolution_error}`,
        );
      } else {
        notifySuccess("WhatsApp desconectado exitosamente.");
      }

      setIsConnected(false);
      setQrCode(null);
      setShowInstructions(false);
      setStatus(null);
      setUserStoppedPolling(false);
      localStorage.removeItem(STORAGE_KEY);

      await fetchStatus();
    } catch (error) {
      handleErrors(error);
    } finally {
      setLoading(false);
    }
  }, [stopStatusPolling, fetchStatus]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    // Auto-crear instancia SOLO si:
    // 1. El status es "not_exists" (no hay instancia en CitoPlus)
    // 2. NO estamos en loading
    // 3. NO hemos intentado auto-crear aún
    // 4. NO hay QR code mostrado
    // 5. El usuario NO ha detenido el polling manualmente
    //
    // NO auto-crear si status es "close" - el usuario debe presionar "Generar QR"
    if (
      status !== null &&
      status.status === WHATSAPP_STATUS.NOT_EXISTS &&
      !loading &&
      !autoCreationAttempted &&
      !qrCode &&
      !userStoppedPolling
    ) {
      setAutoCreationAttempted(true);
      handleCreateInstance();
    }
  }, [
    status,
    loading,
    autoCreationAttempted,
    qrCode,
    userStoppedPolling,
    handleCreateInstance,
  ]);

  useEffect(() => {
    // Cleanup polling controller on component unmount
    return () => {
      if (pollingController) {
        pollingController.stop();
      }
    };
  }, [pollingController]);

  const getStatusBadge = () => {
    if (!status) return null;

    const statusMap = {
      [WHATSAPP_STATUS.CONNECTED]: { variant: "success", text: "Conectado" },
      [WHATSAPP_STATUS.CONNECTING]: { variant: "warning", text: "Conectando" },
      [WHATSAPP_STATUS.WAITING_QR]: { variant: "info", text: "QR Requerido" },
      [WHATSAPP_STATUS.DISCONNECTED]: {
        variant: "danger",
        text: "Desconectado",
      },
      [WHATSAPP_STATUS.ERROR]: { variant: "danger", text: "Error" },
    };

    // Force disconnected status if user stopped polling
    if (userStoppedPolling && !isConnected) {
      return <Badge bg="danger">Desconectado</Badge>;
    }

    const statusInfo = statusMap[status.status] || {
      variant: "secondary",
      text: status.status,
    };

    return <Badge bg={statusInfo.variant}>{statusInfo.text}</Badge>;
  };

  // Show loading while checking policy
  if (policyLoading) {
    return (
      <div className="col-lg-12">
        <Card>
          <Card.Header>
            <h4 className="card-title">WhatsApp QR Configuration</h4>
          </Card.Header>
          <Card.Body className="text-center">
            <SpinnerCitoPlus text="Verificando políticas..." />
          </Card.Body>
        </Card>
      </div>
    );
  }

  // Show policy component if not accepted
  if (!policyAccepted) {
    return <WhatsAppPolicy onAccepted={handlePolicyAccepted} />;
  }

  if (loading && !qrLoading && !isConnected) {
    return (
      <div className="col-lg-12">
        <Card>
          <Card.Header>
            <h4 className="card-title">
              WhatsApp QR Configuration {getStatusBadge()}
            </h4>
          </Card.Header>
          <Card.Body className="text-center">
            <SpinnerCitoPlus text="Configurando WhatsApp..." />
          </Card.Body>
        </Card>
      </div>
    );
  }

  return (
    <div className="col-lg-12">
      <Card>
        <Card.Header>
          <h4 className="card-title">
            WhatsApp QR Configuration {getStatusBadge()}
          </h4>
        </Card.Header>
        <Card.Body>
          {/* STATE 1: WhatsApp is connected - Only show connection info and controls */}
          {isConnected && (
            <div>
              <Alert variant="success" className="mb-3">
                <i className="mdi mdi-check-circle me-2"></i>
                WhatsApp conectado y funcionando correctamente
              </Alert>

              <div className="row mb-3">
                <div className="col-md-6">
                  <div className="info-item mb-2">
                    <strong>Número:</strong>{" "}
                    {status?.data?.phone_number || "No disponible"}
                  </div>
                  {status?.data?.whatsapp_name && (
                    <div className="info-item mb-2">
                      <strong>Nombre:</strong> {status.data.whatsapp_name}
                    </div>
                  )}
                  {status?.data?.instance_name && (
                    <div className="info-item mb-2">
                      <strong>Instancia:</strong> {status.data.instance_name}
                    </div>
                  )}
                </div>
                <div className="col-md-6 text-end">
                  <Button
                    variant="danger"
                    onClick={handleDisconnect}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Desconectando...
                      </>
                    ) : (
                      <>
                        <i className="mdi mdi-logout me-2"></i>
                        Desconectar WhatsApp
                      </>
                    )}
                  </Button>
                </div>
              </div>

              <Alert variant="info" className="text-start">
                <div style={{ fontSize: "0.85rem", lineHeight: "1.4" }}>
                  <strong>📢 WhatsApp de Administración Activo</strong>
                  <hr className="my-2" />
                  <p className="mb-2">
                    ✅ Este WhatsApp está configurado para enviar notificaciones
                    automáticas a los residentes sobre:
                  </p>
                  <ul className="mb-2" style={{ paddingLeft: "1.2rem" }}>
                    <li>🚶‍♂️ Ingreso y salida de visitantes</li>
                    <li>📦 Registro de correspondencia</li>
                    <li>📢 Mensajes masivos de cartelera</li>
                  </ul>
                  <p className="mb-0" style={{ fontSize: "0.8rem" }}>
                    <strong>🔒 Seguridad:</strong> Este WhatsApp debe mantenerse
                    exclusivo para administración.
                  </p>
                </div>
              </Alert>
            </div>
          )}

          {/* STATE 2: WhatsApp not connected */}
          {!isConnected && (
            <div>
              {/* STATE 2a: User stopped polling - Show generate QR button */}
              {userStoppedPolling && (
                <div className="text-center">
                  <Alert variant="warning" className="mb-4">
                    <i className="mdi mdi-information me-2"></i>
                    WhatsApp no está conectado. Configura la conexión generando
                    un código QR.
                  </Alert>

                  <Button
                    variant="primary"
                    onClick={fetchQRCode}
                    disabled={qrLoading}
                    size="lg"
                    className="mb-3"
                  >
                    {qrLoading ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Generando QR...
                      </>
                    ) : (
                      <>
                        <i className="mdi mdi-qrcode me-2"></i>
                        Configurar WhatsApp
                      </>
                    )}
                  </Button>

                  <Alert variant="info" className="text-start">
                    <div style={{ fontSize: "0.85rem", lineHeight: "1.4" }}>
                      <strong>
                        📢 Configuración de WhatsApp de Administración
                      </strong>
                      <hr className="my-2" />
                      <p className="mb-2">
                        Una vez conectado, este WhatsApp enviará notificaciones
                        automáticas sobre:
                      </p>
                      <ul className="mb-2" style={{ paddingLeft: "1.2rem" }}>
                        <li>🚶‍♂️ Ingreso y salida de visitantes</li>
                        <li>📦 Registro de correspondencia</li>
                        <li>📢 Mensajes masivos de cartelera</li>
                      </ul>
                      <p className="mb-0" style={{ fontSize: "0.8rem" }}>
                        <strong>🔒 Importante:</strong> Solo usar número de
                        administración o SIM exclusiva.
                      </p>
                    </div>
                  </Alert>
                </div>
              )}

              {/* STATE 2b: Loading initial setup */}
              {!userStoppedPolling &&
                (loading || (!qrCode && !autoCreationAttempted)) && (
                  <div className="text-center">
                    <div className="mb-4">
                      <SpinnerCitoPlus text="" />
                      <span>Configurando WhatsApp automáticamente...</span>
                    </div>
                    <Alert variant="info" className="text-start">
                      <div style={{ fontSize: "0.85rem", lineHeight: "1.4" }}>
                        <strong>
                          📢 Configurando WhatsApp de Administración
                        </strong>
                        <hr className="my-2" />
                        <p className="mb-2">
                          Se está preparando la conexión para enviar
                          notificaciones automáticas:
                        </p>
                        <ul className="mb-2" style={{ paddingLeft: "1.2rem" }}>
                          <li>🚶‍♂️ Ingreso y salida de visitantes</li>
                          <li>📦 Registro de correspondencia</li>
                          <li>📢 Mensajes masivos de cartelera</li>
                        </ul>
                        <p className="mb-0" style={{ fontSize: "0.8rem" }}>
                          <strong>🔒 Importante:</strong> Solo usar número de
                          administración.
                        </p>
                      </div>
                    </Alert>
                  </div>
                )}

              {/* STATE 2c: QR Code available and instructions */}
              {!userStoppedPolling && showInstructions && qrCode && (
                <div className="whatsapp-qr-section">
                  <div className="row mb-4">
                    <div className="col-12">
                      <Alert variant="warning" className="text-start">
                        <div>
                          <p className="mb-3">
                            El siguiente código QR es para que conecten el
                            WhatsApp del celular de la administración 📱. A
                            través de este número se enviarán a los residentes
                            las notificaciones por WhatsApp, tales como:
                          </p>
                          <div className="row">
                            <div className="col-md-6">
                              <ul className="mb-3">
                                <li>🚶‍♂️ Ingreso y salida de visitantes</li>
                                <li>📦 Registro de correspondencia</li>
                                <li>📢 Mensajes masivos de cartelera</li>
                              </ul>
                            </div>
                            <div className="col-md-6">
                              <div className="alert alert-danger p-2 mb-2">
                                <small>
                                  <strong>⚠️ IMPORTANTE:</strong>
                                  <br />
                                  NO conectar números de portería ni de guardas.
                                </small>
                              </div>
                              <div className="alert alert-light p-2">
                                <small>
                                  <strong>🔒 Alerta de seguridad:</strong>
                                  <br />
                                  Este WhatsApp debe estar vinculado únicamente
                                  a un número de administración o usar una SIM
                                  card exclusiva para garantizar la seguridad de
                                  la información ✅.
                                </small>
                              </div>
                            </div>
                          </div>
                        </div>
                      </Alert>

                      <h5 className="mb-4 text-center text-lg-start">
                        Por favor siga las siguientes instrucciones:
                      </h5>

                      {/* Responsive layout: side by side on desktop, stacked on mobile */}
                      <div className="row">
                        {/* Instructions column */}
                        <div className="col-lg-6 col-12 mb-4 mb-lg-0">
                          <div className="steps-container">
                            <div className="step-item d-flex align-items-start mb-3">
                              <div className="step-number me-3">
                                <span className="badge bg-primary rounded-circle">
                                  1
                                </span>
                              </div>
                              <div className="step-content">
                                <span>
                                  Abre WhatsApp{" "}
                                  <i className="fab fa-whatsapp text-success"></i>{" "}
                                  en tu teléfono.
                                </span>
                              </div>
                            </div>

                            <div className="step-item d-flex align-items-start mb-3">
                              <div className="step-number me-3">
                                <span className="badge bg-primary rounded-circle">
                                  2
                                </span>
                              </div>
                              <div className="step-content">
                                <span>
                                  En Android, toca Menú{" "}
                                  <i className="mdi mdi-dots-vertical"></i>.
                                  <br />
                                  En iPhone, toca Ajustes{" "}
                                  <i className="mdi mdi-cog"></i>.
                                </span>
                              </div>
                            </div>

                            <div className="step-item d-flex align-items-start mb-3">
                              <div className="step-number me-3">
                                <span className="badge bg-primary rounded-circle">
                                  3
                                </span>
                              </div>
                              <div className="step-content">
                                <span>
                                  Toca Dispositivos vinculados y, luego,
                                  Vincular dispositivo.
                                </span>
                              </div>
                            </div>

                            <div className="step-item d-flex align-items-start mb-3">
                              <div className="step-number me-3">
                                <span className="badge bg-primary rounded-circle">
                                  4
                                </span>
                              </div>
                              <div className="step-content">
                                <span>
                                  Escanea el código QR para confirmar.
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* QR Code column */}
                        <div className="col-lg-6 col-12">
                          <div className="qr-container text-center d-flex flex-column align-items-center justify-content-center h-100">
                            {qrLoading ? (
                              <div
                                className="qr-placeholder d-flex align-items-center justify-content-center border rounded"
                                style={{ width: "200px", height: "200px" }}
                              >
                                <SpinnerCitoPlus text="" />
                              </div>
                            ) : qrCode ? (
                              <div className="qr-code-wrapper">
                                <img
                                  src={qrCode}
                                  alt="WhatsApp QR Code"
                                  className="img-fluid border rounded p-2"
                                  style={{
                                    maxWidth: "200px",
                                    maxHeight: "200px",
                                  }}
                                />
                                <div className="mt-2">
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={fetchQRCode}
                                    disabled={qrLoading}
                                    className="me-2"
                                  >
                                    <i className="mdi mdi-refresh me-1"></i>
                                    Generar Nuevo QR
                                  </Button>
                                  {polling && (
                                    <Button
                                      variant="outline-warning"
                                      size="sm"
                                      onClick={stopStatusPolling}
                                    >
                                      <i className="mdi mdi-stop me-1"></i>
                                      Detener verificación
                                    </Button>
                                  )}
                                </div>
                                {polling && (
                                  <div className="mt-2">
                                    <div className="text-center mb-3">
                                      <small className="text-success">
                                        <Spinner size="sm" className="me-1" />
                                        Verificando conexión automáticamente...
                                      </small>
                                    </div>
                                    {countdown && (
                                      <CountdownTimer
                                        remainingSeconds={
                                          countdown.remainingSeconds
                                        }
                                        totalMinutes={5}
                                        variant="primary"
                                        showProgressBar={true}
                                        size="sm"
                                        className="mt-2"
                                      />
                                    )}
                                  </div>
                                )}
                                {!polling && qrCode && (
                                  <div className="mt-2 text-center">
                                    <Button
                                      variant="outline-success"
                                      size="sm"
                                      onClick={fetchStatus}
                                      disabled={loading}
                                    >
                                      <i className="mdi mdi-check-circle me-1"></i>
                                      Verificar si conecté
                                    </Button>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div
                                className="qr-placeholder d-flex align-items-center justify-content-center border rounded text-muted"
                                style={{ width: "200px", height: "200px" }}
                              >
                                <div className="text-center">
                                  <i className="mdi mdi-qrcode-scan display-4"></i>
                                  <br />
                                  <small>Código QR aparecerá aquí</small>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </Card.Body>
      </Card>

      <style jsx>{`
        .step-item {
          position: relative;
        }

        .step-item:not(:last-child)::after {
          content: "";
          position: absolute;
          left: 15px;
          top: 40px;
          width: 2px;
          height: 20px;
          background-color: #dee2e6;
        }

        .step-number .badge {
          width: 30px;
          height: 30px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
        }

        .qr-code-wrapper img {
          background: white;
        }

        .qr-placeholder {
          background-color: #f8f9fa;
        }

        .whatsapp-qr-section {
          background: #f8f9fa;
          padding: 20px;
          border-radius: 8px;
        }

        /* Responsive layout improvements */
        @media (min-width: 992px) {
          .steps-container {
            padding-right: 20px;
          }

          .qr-container {
            border-left: 1px solid #dee2e6;
            padding-left: 20px;
            min-height: 400px;
          }
        }

        @media (max-width: 991px) {
          .steps-container {
            margin-bottom: 20px;
          }

          .qr-container {
            border-top: 1px solid #dee2e6;
            padding-top: 20px;
          }
        }

        .steps-container {
          background: white;
          border-radius: 8px;
          padding: 20px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .qr-container {
          background: white;
          border-radius: 8px;
          padding: 20px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }
      `}</style>
    </div>
  );
};

export default WhatsAppQRAdmin;
